import { useState, useCallback } from 'react';
import * as _ from 'lodash';
import type { TextInputOnChange } from '../components/generic/Forms/InputBase';

/**
 * A simple container for input values.
 * Note: this assumes that you are using our TextInput component,
 * which
 *
 * @param {string} [initialValue] - Initial value on component mount.
 * @param {TextInputOnChange} [onChangeHook] - Optionally hook into the onChange event, getting the value.
 * @returns {[string, TextInputOnChange]} - Returns the value and the onChange, to be passed to <TextInput />
 * @example
 * import React from 'react';
 * import { TextInput } from '../generic/Forms';
 * import { useInputValue } from '../../hooks';
 *
 * const MyComponent = () => {
 *   const [value, onChange] = useInputValue();
 *   return (
 *     <div>
 *       <h1>{value ? `Hi ${value}` : 'Enter your name'}</h1>
 *       <TextInput
 *         onChange={onChange}
 *         value={value}
 *         placeholder="Enter name"
 *       />
 *     </div>
 *   );
 * };
 */
const useInputValue = (initialValue: string = '', onChangeHook: TextInputOnChange = _.noop): [string, TextInputOnChange] => {
  const [value, setValue] = useState<string>(initialValue);
  const onChange: TextInputOnChange = useCallback((nextValue, event) => {
    setValue(nextValue);
    onChangeHook(nextValue, event);
  }, [onChangeHook]);
  return [
    value,
    onChange,
  ];
};

export default useInputValue;
